import React from 'react';
import type { Workspace } from '@/types';
import { SectionHeading, WorkspaceStat } from './primitives';
import { workspaceTileColor } from './profileConstants';

interface WorkspaceAccessCardProps {
  availableWorkspaces: Workspace[];
  currentWorkspace: Workspace | null;
  onSelectWorkspace: (ws: Workspace) => void;
}

const WorkspaceAccessCard: React.FC<WorkspaceAccessCardProps> = ({
  availableWorkspaces,
  currentWorkspace,
  onSelectWorkspace,
}) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8 dark:border-gray-700 dark:bg-gray-800">
    <SectionHeading
      icon={
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
        </svg>
      }
    >
      Workspace Access
      <span className="ml-1 font-normal normal-case tracking-normal text-gray-400 dark:text-gray-500">
        · {availableWorkspaces.length} {availableWorkspaces.length === 1 ? 'workspace' : 'workspaces'}
      </span>
    </SectionHeading>
    <p className="-mt-2 mb-4 text-sm text-gray-500 dark:text-gray-400">
      Select which workspace you’re working in — this controls the data shown across the app.
    </p>

    {availableWorkspaces.length === 0 ? (
      <div className="rounded-xl border border-dashed border-gray-200 py-8 text-center dark:border-gray-700">
        <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No workspace access</p>
        <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
          You don’t have access to any workspaces yet. Contact an administrator.
        </p>
      </div>
    ) : (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {availableWorkspaces.map(ws => {
          const active = ws.id === currentWorkspace?.id;
          return (
            <button
              key={ws.id}
              type="button"
              onClick={() => onSelectWorkspace(ws)}
              aria-pressed={active}
              className={`flex items-start gap-3 rounded-xl border p-4 text-left transition ${
                active
                  ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500 dark:border-primary-400 dark:bg-primary-900/20 dark:ring-primary-400'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/40'
              }`}
            >
              <span
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-white shadow-sm ${
                  active ? 'bg-primary-600' : workspaceTileColor(ws.id)
                }`}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l7 2.4v5.1c0 4.6-3.1 7.4-7 8.5-3.9-1.1-7-3.9-7-8.5V5.4L12 3z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.3 11.7l1.9 1.9 3.6-3.6" />
                </svg>
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{ws.name}</span>
                  {ws.wyscoutTeamId != null && (
                    <span className="shrink-0 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                      Wyscout
                    </span>
                  )}
                </span>
                {ws.description && (
                  <span className="mt-0.5 block truncate text-xs text-gray-500 dark:text-gray-400">{ws.description}</span>
                )}
                {ws.playerCount !== undefined && (
                  <span className="mt-2 flex flex-wrap items-center gap-1.5">
                    <WorkspaceStat
                      value={ws.playerCount}
                      label="players"
                      icon={
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.1v-1.3a3.8 3.8 0 00-3.8-3.8H6.8A3.8 3.8 0 003 17.8v1.3M21 19.1v-1.3a3.8 3.8 0 00-2.8-3.7M15.8 4.9a3.8 3.8 0 010 7.3M9 11.6A3.8 3.8 0 109 4a3.8 3.8 0 000 7.6z" />
                        </svg>
                      }
                    />
                    <WorkspaceStat
                      value={ws.matchCount ?? 0}
                      label="matches"
                      icon={
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0V11.25A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                        </svg>
                      }
                    />
                    <WorkspaceStat
                      value={ws.staffCount ?? 0}
                      label="staff"
                      icon={
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.7a3 3 0 00-6 0m9.8 0a3 3 0 00-3.3-3M2.2 18.7a3 3 0 013.3-3m12.4-3.3a2.6 2.6 0 10-3.7-3.7M9.6 8.5a2.6 2.6 0 11-3.7 0M16.6 14a3 3 0 00-9.2 0" />
                        </svg>
                      }
                    />
                  </span>
                )}
                <span
                  className={`mt-1.5 inline-block text-xs font-medium ${
                    active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'
                  }`}
                >
                  {active ? 'Active workspace' : 'Click to switch'}
                </span>
              </span>
              {active && (
                <svg className="h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                  <path fillRule="evenodd" d="M16.7 6.3a1 1 0 010 1.4l-7 7a1 1 0 01-1.4 0l-3-3a1 1 0 111.4-1.4L9 12.6l6.3-6.3a1 1 0 011.4 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          );
        })}
      </div>
    )}
  </div>
);

export default WorkspaceAccessCard;
